import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { chatApi } from "../api";
import { useLang, type Bilingual } from "../lang";
import { refusal } from "./SpecCard";

export interface LibraryTemplate {
  key: string;
  area: string;
  name: Bilingual;
  blurb: Bilingual;
  in_words: Bilingual;
}
/** An agent built by the team and published to the library (agent-library Task 13b). */
export interface LibraryTeamEntry {
  id: string;
  area: string | null;
  name: Bilingual;
  blurb: Bilingual;
  in_words: Bilingual;
  roles: string[];
  roles_words: Bilingual;
  /** This person's role may use it without a new request. */
  ready: boolean;
  /** The original is not Active now: no new copies until it is. */
  on_hold?: boolean;
  /** "Built by <name> · used by <n> · checks passed", written by code. */
  line: Bilingual;
}
/** The thread line's `meta.library` (agent-library Task 7). */
export interface LibraryMeta {
  /** The area the person named, when they named one. */
  area?: string;
  areas: { key: string; label: Bilingual }[];
  templates: LibraryTemplate[];
  /** "Built by your team" (Task 13b); absent on cards written before it. */
  team?: LibraryTeamEntry[];
}

const TEAM = {
  ready: { en: "Ready-made", ar: "جاهزة" },
  team: { en: "Built by your team", ar: "من بناء فريقك" },
  noAsk: { en: "Already approved for your role.", ar: "تمت الموافقة عليه لدورك مسبقاً." },
  willAsk: { en: "Your Supervisor will be asked for your copy.", ar: "سيُطلب من المشرف الموافقة على نسختك." },
  onHold: { en: "On hold until the original passes its checks again.", ar: "موقوف مؤقتاً حتى يجتاز الأصل فحوصه مجدداً." },
} satisfies Record<string, Bilingual>;

/**
 * Agents to start from, in two sections: "Ready-made" — one tab per area,
 * each template's name and what it tells you, and "Use this", which makes a
 * draft for this person only — and "Built by your team" (Task 13b), where
 * "Use this" makes this person's own copy, ready at once when their role was
 * approved for it. Either way the spec card, written by code, arrives in the
 * thread.
 */
export function LibraryCard({ workspaceId, library }: { workspaceId: string; library: LibraryMeta }) {
  const { t, lang } = useLang();
  const qc = useQueryClient();
  const areas = library.areas.filter((a) => library.templates.some((x) => x.area === a.key));
  const [area, setArea] = useState(library.area ?? areas[0]?.key ?? "");
  const use = useMutation({
    mutationFn: (key: string) => chatApi.useTemplate(workspaceId, key),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["chat-thread", workspaceId] }),
  });
  const useTeam = useMutation({
    mutationFn: (id: string) => chatApi.useTeamEntry(workspaceId, id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["chat-thread", workspaceId] }),
  });
  const shown = library.templates.filter((x) => x.area === area);
  const team = library.team ?? [];

  return (
    <div className="pwa-card pwa-library" data-testid="library-card">
      <div className="ttl">{t({ en: "Agents you can start from", ar: "وكلاء يمكنك البدء منها" })}</div>
      {team.length > 0 && shown.length > 0 ? <div className="pwa-fork-prepared-label">{t(TEAM.ready)}</div> : null}
      {areas.length > 1 ? (
        <div className="pwa-chips" role="tablist" aria-label={t({ en: "Areas", ar: "المجالات" })}>
          {areas.map((a) => (
            <button
              key={a.key}
              type="button"
              role="tab"
              aria-selected={a.key === area}
              className={`pwa-chip${a.key === area ? " on" : ""}`}
              onClick={() => setArea(a.key)}
            >
              {t(a.label)}
            </button>
          ))}
        </div>
      ) : null}
      {shown.length > 0 ? (
        <ul className="pwa-library-list" role="tabpanel">
          {shown.map((x) => (
            <li key={x.key} className="pwa-library-item">
              <div className="pwa-library-name">{t(x.name)}</div>
              <div className="sub">{t(x.blurb)}</div>
              <button
                type="button"
                className="pwa-btn"
                data-testid={`use-${x.key}`}
                disabled={use.isPending}
                onClick={() => use.mutate(x.key)}
              >
                {t({ en: "Use this", ar: "استخدم هذا" })}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {team.length > 0 ? (
        <section aria-label={t(TEAM.team)} data-testid="library-team">
          <div className="pwa-fork-prepared-label">{t(TEAM.team)}</div>
          <ul className="pwa-library-list">
            {team.map((x) => (
              <li key={x.id} className="pwa-library-item">
                <div className="pwa-library-name">{t(x.name)}</div>
                <div className="sub">{t(x.blurb)}</div>
                <div className="sub" data-testid={`team-line-${x.id}`}>
                  {t(x.line)}
                </div>
                <div className="sub">{t(x.on_hold ? TEAM.onHold : x.ready ? TEAM.noAsk : TEAM.willAsk)}</div>
                <button
                  type="button"
                  className="pwa-btn"
                  data-testid={`use-team-${x.id}`}
                  disabled={useTeam.isPending || x.on_hold === true}
                  onClick={() => useTeam.mutate(x.id)}
                >
                  {t({ en: "Use this", ar: "استخدم هذا" })}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {use.isError || useTeam.isError ? (
        <div className="pwa-fork-effect" role="status">
          {refusal(use.error ?? useTeam.error, lang)}
        </div>
      ) : null}
    </div>
  );
}
